import { initReactI18next } from 'react-i18next';

import i18n, { BackendModule } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English, the site's language: the text every page needs is in the main bundle
import enTranslation from './locales/en/translation.json';

import { SUPPORTED_LANGUAGES } from './utils/languageConfig';

// The text every page needs
const CORE_FILES = ['translation'] as const;

// Big sections only a few pages use: loaded by those pages (useTranslationSections), into the same namespace (keys
// like 'pages.ryman.hero.title'). 'pages' is the Business, Ryman, Mail Boxes and Students pages
export type TranslationSection = 'shopLocations' | 'blog' | 'printingGuides' | 'legalPages' | 'pages' | 'environment';

// One file of one language, as its own chunk
const loadFile = (language: string, file: string) =>
  import(`./locales/${language}/${file}.json`).then((module) => module.default as Record<string, unknown>);

// Other languages (when they're switched on): their core text is fetched when they're first used
const lazyLanguages: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language, _namespace, callback) => {
    Promise.all(CORE_FILES.map((file) => loadFile(language, file)))
      .then((parts) => callback(null, Object.assign({}, ...parts)))
      .catch((error) => callback(error, null));
  },
};

const loadedSections = new Set<string>();

// Adds the sections' text for a language (and English, the fallback) once
export const loadTranslationSections = (language: string, sections: TranslationSection[]) => {
  const languages = Array.from(new Set([language, 'en']));
  return Promise.all(
    languages.flatMap((lng) =>
      sections
        .filter((section) => !loadedSections.has(`${lng}:${section}`))
        .map((section) =>
          loadFile(lng, section).then((data) => {
            i18n.addResourceBundle(lng, 'translation', data, true, true);
            loadedSections.add(`${lng}:${section}`);
          }),
        ),
    ),
  );
};

export const sectionsLoaded = (language: string, sections: TranslationSection[]) =>
  sections.every((section) => loadedSections.has(`${language}:${section}`));

i18n
  .use(lazyLanguages)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslation },
    },
    // The bundled English is complete; other languages come from the backend
    partialBundledLanguages: true,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    debug: false,
    // Re-render when a section's text arrives
    react: { bindI18nStore: 'added' },
  });

export default i18n;
